import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faCircleXmark, faCircleCheck, faCircleExclamation, faPause, faStop, faPlus, faArchive, faTimesCircle, faFilter, faSearch, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import SuccessModal from '../../components/SuccessModal';

const Interviewers = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [interviewers, setInterviewers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedInterviewer, setSelectedInterviewer] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    
    // Search and filter states (applied filters)
    const [searchTerm, setSearchTerm] = useState('');
    const [zoneFilter, setZoneFilter] = useState('');
    const [villeFilter, setVilleFilter] = useState('');
    const [performanceFilter, setPerformanceFilter] = useState('');
    const [verifiedFilter, setVerifiedFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    
    // Local filter states (not applied until button is clicked)
    const [searchInput, setSearchInput] = useState('');
    const [localZoneFilter, setLocalZoneFilter] = useState('');
    const [localVilleFilter, setLocalVilleFilter] = useState('');
    const [localPerformanceFilter, setLocalPerformanceFilter] = useState('');
    const [localVerifiedFilter, setLocalVerifiedFilter] = useState('');
    const [localTypeFilter, setLocalTypeFilter] = useState('');
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalInterviewers, setTotalInterviewers] = useState(0);
    const interviewersPerPage = 10;
    
    // Get unique villes for filter dropdown
    const [availableVilles, setAvailableVilles] = useState([]);

    useEffect(() => {
        fetchInterviewers();
        fetchAvailableVilles();
    }, [currentPage, zoneFilter, villeFilter, performanceFilter, verifiedFilter, typeFilter, searchTerm]);

    const fetchInterviewers = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: currentPage,
                per_page: interviewersPerPage
            });
            
            if (searchTerm) {
                params.append('search', searchTerm);
            }
            if (zoneFilter) {
                params.append('zone', zoneFilter);
            }
            if (villeFilter) {
                params.append('ville', villeFilter);
            }
            if (performanceFilter) {
                params.append('performance', performanceFilter);
            }
            if (verifiedFilter !== '') {
                params.append('verified', verifiedFilter);
            }
            if (typeFilter) {
                params.append('type', typeFilter);
            }
            
            const response = await api.get(`/interviewers?${params.toString()}`);
            if (response.data.success) {
                setInterviewers(response.data.data || []);
                setTotalPages(response.data.last_page || 1);
                setTotalInterviewers(response.data.total || 0);
            } else {
                setError('Erreur lors du chargement des enquêteurs');
            }
        } catch (error) {
            setError('Erreur lors du chargement des enquêteurs');
            console.error('Error fetching interviewers:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableVilles = async () => {
        try {
            // Fetch all interviewers to get unique villes
            const response = await api.get('/interviewers');
            if (response.data.success) {
                const allInterviewers = response.data.data || [];
                const villesSet = new Set();
                allInterviewers.forEach(interviewer => {
                    if (Array.isArray(interviewer.ville)) {
                        interviewer.ville.forEach(ville => villesSet.add(ville));
                    }
                });
                setAvailableVilles(Array.from(villesSet).sort());
            }
        } catch (error) {
            console.error('Error fetching villes:', error);
        }
    };

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1); // Reset to first page when searching
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleFilterChange = (filterType, value) => {
        switch (filterType) {
            case 'zone':
                setLocalZoneFilter(value);
                break;
            case 'ville':
                setLocalVilleFilter(value);
                break;
            case 'performance':
                setLocalPerformanceFilter(value);
                break;
            case 'verified':
                setLocalVerifiedFilter(value);
                break;
            case 'type':
                setLocalTypeFilter(value);
                break;
        }
    };

    const handleApplyFilters = () => {
        setZoneFilter(localZoneFilter);
        setVilleFilter(localVilleFilter);
        setPerformanceFilter(localPerformanceFilter);
        setVerifiedFilter(localVerifiedFilter);
        setTypeFilter(localTypeFilter);
        setCurrentPage(1); // Reset to first page when applying filters
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleDeleteClick = (interviewer) => {
        setSelectedInterviewer(interviewer);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async (reason) => {
        try {
            await api.delete(`/interviewers/${selectedInterviewer.id}`, {
                data: { archive_reason: reason }
            });
            setSuccessMessage('Enquêteur archivé avec succès');
            setShowSuccessModal(true);
            setShowDeleteModal(false);
            // Refresh the list
            fetchInterviewers();
        } catch (error) {
            setSuccessMessage('Erreur lors de l\'archivage de l\'enquêteur');
            setShowSuccessModal(true);
        }
    };

    const handleEditClick = (interviewer) => {
        navigate(`/interviewers/${interviewer.id}?edit=true`);
    };

    const clearFilters = () => {
        setSearchInput('');
        setSearchTerm('');
        setLocalZoneFilter('');
        setLocalVilleFilter('');
        setLocalPerformanceFilter('');
        setLocalVerifiedFilter('');
        setLocalTypeFilter('');
        setZoneFilter('');
        setVilleFilter('');
        setPerformanceFilter('');
        setVerifiedFilter('');
        setTypeFilter('');
        setCurrentPage(1);
    };

    if (loading && interviewers.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 relative overflow-hidden flex items-center justify-center">
                {/* Dotted Background */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>
                </div>
                <div className="text-center relative z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement des enquêteurs...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden py-8">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white shadow-xl rounded-lg">
                    <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-200">
                        {/* Title and Action Buttons */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            {/* Title and Subtitle */}
                            <div>
                                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Gestion des Enquêteurs</h1>
                                <p className="mt-1 text-sm text-gray-600">
                                    Gérez les enquêteurs de terrain
                                </p>
                            </div>
                            
                            {/* Action Buttons — hidden for read-only roles (resp_controle) */}
                            {user?.role !== 'resp_controle' && (
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                <Link
                                    to="/interviewers/create"
                                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center justify-center sm:justify-start"
                                >
                                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                    Nouvel Enquêteur
                                </Link>
                                <Link
                                    to="/interviewers/archived"
                                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 flex items-center justify-center sm:justify-start"
                                >
                                    <FontAwesomeIcon icon={faArchive} className="mr-2" />
                                    Voir les Archivés
                                </Link>
                            </div>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="px-6 py-4 bg-red-50 border-l-4 border-red-400">
                            <p className="text-red-700">{error}</p>
                        </div>
                    )}

                    {/* Search and Filters */}
                    <div className="px-4 sm:px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center space-x-2">
                                <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
                                <span className="font-medium text-gray-700">Filtres:</span>
                            </div>
                            
                            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${user?.role === 'admin_it' ? 'xl:grid-cols-7' : 'xl:grid-cols-6'} gap-3`}>
                                {/* Search */}
                                <div className="sm:col-span-2 lg:col-span-3 xl:col-span-2 flex gap-2 min-w-0">
                                    <input
                                        type="text"
                                        placeholder="Rechercher par nom, email ou référence..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        onKeyPress={handleSearchKeyPress}
                                        className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                    />
                                    <button
                                        onClick={handleSearch}
                                        className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center flex-shrink-0"
                                    >
                                        <FontAwesomeIcon icon={faSearch} className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Type Filter (CATI/F2F) - Only for admin_it */}
                                {user?.role === 'admin_it' && (
                                    <select
                                        value={localTypeFilter}
                                        onChange={(e) => handleFilterChange('type', e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                    >
                                        <option value="">Tous les types</option>
                                        <option value="cati">CATI</option>
                                        <option value="f2f">Face-to-Face</option>
                                    </select>
                                )}

                                {/* Zone Filter */}
                                <select
                                    value={localZoneFilter}
                                    onChange={(e) => handleFilterChange('zone', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                >
                                    <option value="">Toutes les zones</option>
                                    <option value="rural">Rural</option>
                                    <option value="urbain">Urbain</option>
                                </select>

                                {/* Ville Filter */}
                                <select
                                    value={localVilleFilter}
                                    onChange={(e) => handleFilterChange('ville', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                >
                                    <option value="">Toutes les villes</option>
                                    {availableVilles.map((ville) => (
                                        <option key={ville} value={ville}>{ville}</option>
                                    ))}
                                </select>

                                {/* Performance Filter */}
                                <select
                                    value={localPerformanceFilter}
                                    onChange={(e) => handleFilterChange('performance', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                >
                                    <option value="">Toutes les performances</option>
                                    <option value="excellent">Excellent (≥80%)</option>
                                    <option value="good">Bon (60-79%)</option>
                                    <option value="average">Moyen (40-59%)</option>
                                    <option value="poor">Faible (&lt;40%)</option>
                                    <option value="no_rating">Sans note</option>
                                </select>

                                {/* Verified Filter */}
                                <select
                                    value={localVerifiedFilter}
                                    onChange={(e) => handleFilterChange('verified', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                >
                                    <option value="">Tous les statuts</option>
                                    <option value="yes">Vérifié</option>
                                    <option value="partially">Partiellement vérifié</option>
                                    <option value="no">Non vérifié</option>
                                </select>
                            </div>

                            {/* Filter Actions */}
                            <div className="flex gap-2 items-center">
                                <button
                                    onClick={handleApplyFilters}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center text-sm font-medium"
                                >
                                    <FontAwesomeIcon icon={faFilter} className="mr-2" />
                                    Appliquer les filtres
                                </button>
                                
                                {(searchTerm || zoneFilter || villeFilter || performanceFilter || verifiedFilter !== '' || typeFilter || localZoneFilter || localVilleFilter || localPerformanceFilter || localVerifiedFilter !== '' || localTypeFilter) && (
                                    <button
                                        onClick={clearFilters}
                                        className="px-4 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        Réinitialiser
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Référence
                                    </th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Nom
                                    </th>
                                    <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Zone
                                    </th>
                                    <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Villes
                                    </th>
                                    <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Zones Locales
                                    </th>
                                    <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Prestataire
                                    </th>
                                    <th className="hidden xl:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Performance
                                    </th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Statut
                                    </th>
                                    <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Note Contrôle
                                    </th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Vérifié
                                    </th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {interviewers.map((interviewer) => (
                                    <tr 
                                        key={interviewer.id} 
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => navigate(`/interviewers/${interviewer.id}`)}
                                    >
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {interviewer.ref}
                                        </td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {interviewer.name}
                                        </td>
                                        <td className="hidden sm:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {interviewer.email}
                                        </td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {interviewer.zone ? (
                                                <span className={`px-2 py-1 text-xs rounded-full ${
                                                    interviewer.zone === 'rural' 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {interviewer.zone === 'rural' ? 'Rural' : 'Urbain'}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="hidden md:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {Array.isArray(interviewer.ville) && interviewer.ville.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {interviewer.ville.map((ville, index) => (
                                                        <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                                            {ville}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td className="hidden lg:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {Array.isArray(interviewer.zone_locales) && interviewer.zone_locales.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {interviewer.zone_locales.map((zone, index) => (
                                                        <span key={index} className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                                                            {zone}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td className="hidden lg:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {!interviewer.is_cati && (interviewer.supervisor?.name || interviewer.supervisor_name) ? (
                                                <span className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded-full">
                                                    {interviewer.supervisor?.name || interviewer.supervisor_name}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="hidden xl:table-cell px-3 sm:px-6 py-4 whitespace-nowrap">
                                            {interviewer.accuracy_rating !== null && interviewer.accuracy_rating !== undefined ? (
                                                <div className="flex items-center">
                                                    <div className="bg-gray-200 rounded-full h-4" style={{ width: '125px' }}>
                                                        <div 
                                                            className={`h-4 rounded-full ${
                                                                interviewer.accuracy_rating >= 80 ? 'bg-green-600' :
                                                                interviewer.accuracy_rating >= 60 ? 'bg-yellow-600' :
                                                                'bg-red-600'
                                                            }`}
                                                            style={{ width: `${interviewer.accuracy_rating}%` }}
                                                        />
                                                    </div>
                                                    <span className="ml-2 text-sm font-medium text-gray-700">
                                                        {Math.round(interviewer.accuracy_rating)}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-sm text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-3">
                                                <div className="relative group">
                                                    <FontAwesomeIcon 
                                                        icon={
                                                            interviewer.status === 'active' 
                                                                ? faCircleCheck 
                                                                : interviewer.status === 'suspended'
                                                                ? faPause
                                                                : faTimesCircle
                                                        }
                                                        className={
                                                            interviewer.status === 'active' 
                                                                ? 'text-green-600' 
                                                                : interviewer.status === 'suspended'
                                                                ? 'text-red-600'
                                                                : 'text-yellow-600'
                                                        }
                                                    />
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                        {interviewer.status === 'active' ? 'Actif' : 
                                                         interviewer.status === 'suspended' ? 'Suspendu' : 'Inactif'}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="hidden md:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
                                            {interviewer.controllers_rating != null ? (() => {
                                                const avg = (parseFloat(interviewer.controllers_rating) / 100) * 5;
                                                return (
                                                    <span className={`font-semibold ${
                                                        avg >= 4 ? 'text-green-600' :
                                                        avg >= 3 ? 'text-yellow-600' :
                                                        'text-red-600'
                                                    }`}>
                                                        {avg.toFixed(1)}/5
                                                    </span>
                                                );
                                            })() : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-3">
                                                <div className="relative group">
                                                    <FontAwesomeIcon
                                                        icon={
                                                            interviewer.verified === 'yes'
                                                                ? faCircleCheck
                                                                : interviewer.verified === 'partially'
                                                                ? faCircleExclamation
                                                                : faCircleXmark
                                                        }
                                                        className={
                                                            interviewer.verified === 'yes' 
                                                                ? 'text-green-600' 
                                                                : interviewer.verified === 'partially'
                                                                ? 'text-yellow-600'
                                                                : 'text-gray-500'
                                                        }
                                                    />
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                        {interviewer.verified === 'yes' ? 'Vérifié' : 
                                                         interviewer.verified === 'partially' ? 'Partiellement vérifié' : 'Non vérifié'}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            {user?.role === 'resp_controle' ? (
                                                <span className="text-gray-400 text-xs italic">Lecture seule</span>
                                            ) : (
                                            <div className="flex space-x-3">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditClick(interviewer);
                                                    }}
                                                    className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50 transition-colors"
                                                    title="Modifier"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                {/* Bouton Archiver/Supprimer retiré de la liste — l'archivage se fait
                                                    depuis la fiche de l'enquêteur. Décommenter pour le rétablir.
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(interviewer);
                                                    }}
                                                    className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50 transition-colors"
                                                    title="Archiver"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                                */}
                                            </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {interviewers.length === 0 && !loading && (
                        <div className="text-center py-12">
                            <p className="text-gray-500">Aucun enquêteur trouvé</p>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && totalPages > 1 && (
                        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                                <span>
                                    Affichage de {((currentPage - 1) * interviewersPerPage) + 1} à {Math.min(currentPage * interviewersPerPage, totalInterviewers)} sur {totalInterviewers} enquêteurs
                                </span>
                            </div>
                            
                            <div className="flex items-center justify-center sm:justify-end gap-2 flex-wrap">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-2 sm:px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                                </button>
                                
                                <div className="flex gap-1 flex-wrap justify-center">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors flex-shrink-0 ${
                                                    currentPage === pageNum
                                                        ? 'bg-red-600 text-white'
                                                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-2 sm:px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                                >
                                    <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                interviewerName={selectedInterviewer?.name}
            />

            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                message={successMessage}
                duration={3000}
            />
        </div>
    );
};

export default Interviewers;
